"use client"
import { useEffect, useState } from "react"
import { Input, Space, Button, DatePicker } from "antd"
import { useGlobalContext } from "../../lib/storage"
import moment from "moment"
import "../../styles/app.css"
import "../../styles/antd.css"
import ThemeJson from "../../constants/UIServer.json"

const AnalyticsReportTable = (props) => {
  const { globalStore } = useGlobalContext()
  const Themes = ThemeJson
  const genericUrl = process.env.REACT_APP_genericUrl
  const {
    columnsData,
    gridData,
    reportFields,
    nestedTableFlag,
    parametersToSend,
    mainReportId,
    detailedReportId,
    viewMoreLessFilter,
    reportFiltersForNextRowLength,
    totalRecords,
    fullScreen,
    groupingConfiguration,
    columnGrouping,
    onDrillDown,
    onNavigation,
  } = props

  const [columns, setColumns] = useState([])
  const [flattenedData, setFlattenedData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [filters, setFilters] = useState({})
  const [showFilterDropdown, setShowFilterDropdown] = useState({})
  const [groupedColumns, setGroupedColumns] = useState([])
  const [ungroupedColumns, setUngroupedColumns] = useState([])
  const [orderedColumns, setOrderedColumns] = useState([])

  console.log(columnGrouping)

  const appId = localStorage.getItem("appId")
  const userPreferences = JSON.parse(localStorage.getItem("userPreferences"))
  const roundOffDecimalValue = userPreferences?.decimalPlaces || 2
  const dateFormat = userPreferences?.dateFormat || "DD-MM-YYYY"
  const amountArray = []

  // Identify Amount fields for formatting
  for (let index = 0; index < reportFields.length; index++) {
    const element = reportFields[index]
    if (element.type === "Amount") {
      amountArray.push(element.fieldName)
    }
  }

  // Format numeric fields in gridData
  if (gridData.length > 0) {
    for (let index = 0; index < gridData.length; index++) {
      const element = gridData[index]
      for (let index2 = 0; index2 < amountArray.length; index2++) {
        const element2 = amountArray[index2]
        element[element2] = Number.parseFloat(element[element2] || 0)
      }
    }
  }

  // Process column grouping and create ordered columns
  useEffect(() => {
    if (columnGrouping && columnGrouping.length > 0) {
      const grouped = []
      const ungrouped = []

      // Sort column groups by sequence
      const sortedGroups = [...columnGrouping].sort((a, b) => a.sequence - b.sequence)

      sortedGroups.forEach((group) => {
        const groupColumns = columnsData.filter((col) => group.columns.includes(col.dataIndex))

        if (groupColumns.length > 0) {
          grouped.push({
            ...group,
            columns: groupColumns,
          })
        }
      })

      // Find columns that are not in any group
      const groupedColumnIds = columnGrouping.flatMap((group) => group.columns)
      const ungroupedCols = columnsData.filter((col) => !groupedColumnIds.includes(col.dataIndex))

      setGroupedColumns(grouped)
      setUngroupedColumns(ungroupedCols)

      // Create ordered columns array: grouped columns first (by sequence), then ungrouped
      const ordered = []

      // Add grouped columns first, in sequence order
      grouped.forEach((group) => {
        group.columns.forEach((column) => {
          ordered.push(column)
        })
      })

      // Add ungrouped columns at the end
      ungroupedCols.forEach((column) => {
        ordered.push(column)
      })

      setOrderedColumns(ordered)
    } else {
      setGroupedColumns([])
      setUngroupedColumns(columnsData)
      setOrderedColumns(columnsData)
    }
  }, [columnsData, columnGrouping])

  // Flatten grouped data for display
  useEffect(() => {
    const levels = groupingConfiguration?.levels || []
    if (levels.length === 0 || gridData.length === 0) {
      const mapped = gridData.map((item, index) => ({ ...item, key: index, level: 0, isGroup: false }))
      setFlattenedData(mapped)
      setFilteredData(mapped)
      return
    }

    const flattenGroupedData = (data, levelIndex, parentKey = "", result = []) => {
      if (levelIndex >= levels.length) {
        data.forEach((item, index) => {
          result.push({
            ...item,
            key: `${parentKey}_${index}`,
            level: levelIndex,
            isGroup: false,
          })
        })
        return result
      }

      const level = levels[levelIndex]
      const groupField = level.fieldName
      const sortOrder = level.sortOrder || "ASC"
      const showSubtotal = level.showSubtotal

      // Group data by the current level's field
      const grouped = {}
      data.forEach((item) => {
        const groupValue = item[groupField] || "N/A"
        if (!grouped[groupValue]) {
          grouped[groupValue] = []
        }
        grouped[groupValue].push(item)
      })

      // Process groups in sorted order
      Object.keys(grouped)
        .sort((a, b) => {
          if (sortOrder === "ASC") return a.localeCompare(b)
          return b.localeCompare(a)
        })
        .forEach((groupValue, groupIndex) => {
          const groupItems = grouped[groupValue]
          const groupKey = `${parentKey}_${groupValue}_${groupIndex}`

          // Add group header row
          const groupHeaderData = {}
          reportFields.forEach((field) => {
            if (field.fieldName === groupField) {
              groupHeaderData[field.fieldName] = groupValue.toUpperCase()
            } else {
              groupHeaderData[field.fieldName] = ""
            }
          })

          // Calculate group total
          let groupTotal = 0
          if (showSubtotal) {
            reportFields.forEach((field) => {
              if (field.type === "Numeric" || field.type === "Amount") {
                const total = groupItems.reduce((sum, item) => sum + Number.parseFloat(item[field.fieldName] || 0), 0)
                if (field.type === "Amount") {
                  groupTotal = total
                }
              }
            })
          }

          result.push({
            ...groupHeaderData,
            key: groupKey,
            level: levelIndex,
            isGroup: true,
            isGroupHeader: true,
            groupTotal: groupTotal,
            groupField: groupField,
          })

          // Recursively process children
          flattenGroupedData(groupItems, levelIndex + 1, groupKey, result)

          // Add subtotal row after each group (always show subtotals)
          const subtotalData = {}
          let hasNumericData = false

          reportFields.forEach((field) => {
            if (field.type === "Numeric" || field.type === "Amount") {
              const total = groupItems.reduce((sum, item) => sum + Number.parseFloat(item[field.fieldName] || 0), 0)
              subtotalData[field.fieldName] = total
              if (total > 0) hasNumericData = true
            } else if (field.fieldName === groupField) {
              subtotalData[field.fieldName] = `${groupValue} Total`
            } else {
              subtotalData[field.fieldName] = ""
            }
          })

          // Only add subtotal if there's numeric data and it's not the last level
          if (hasNumericData && levelIndex < levels.length) {
            result.push({
              ...subtotalData,
              key: `${groupKey}_subtotal`,
              level: levelIndex,
              isGroup: true,
              isSubtotal: true,
              groupField: groupField,
            })
          }
        })

      return result
    }

    const flattened = flattenGroupedData(gridData, 0)
    setFlattenedData(flattened)
    setFilteredData(flattened)
  }, [gridData, groupingConfiguration, reportFields])

  // Set up columns
  useEffect(() => {
    setColumns([...orderedColumns])
  }, [orderedColumns])

  // Apply filters
  useEffect(() => {
    let filtered = [...flattenedData]

    Object.keys(filters).forEach((columnKey) => {
      const filterValue = filters[columnKey]
      if (filterValue && filterValue.trim() !== "") {
        filtered = filtered.filter((record) => {
          const cellValue = record[columnKey]
          if (cellValue === null || cellValue === undefined) return false
          return cellValue.toString().toLowerCase().includes(filterValue.toLowerCase())
        })
      }
    })

    setFilteredData(filtered)
  }, [filters, flattenedData])

  const handleFilterChange = (columnKey, value) => {
    setFilters((prev) => ({
      ...prev,
      [columnKey]: value,
    }))
  }

  const handleFilterReset = (columnKey) => {
    setFilters((prev) => {
      const newFilters = { ...prev }
      delete newFilters[columnKey]
      return newFilters
    })
    setShowFilterDropdown((prev) => ({
      ...prev,
      [columnKey]: false,
    }))
  }

  const toggleFilterDropdown = (columnKey) => {
    setShowFilterDropdown((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }))
  }

  const renderCell = (record, column) => {
    const text = record[column.dataIndex]
    const isNumericOrAmount = column.type === "Numeric" || column.type === "Amount"
    const textAlign = isNumericOrAmount ? "right" : "left"

    // Handle group header rows
    if (record.isGroup && record.isGroupHeader) {
      if (column.dataIndex === record.groupField) {
        return (
          <div
            style={{
              fontWeight: 400,
              color: "#000000",
              textAlign: "left",
              fontFamily: "Inter, sans-serif",
              fontSize: "9px",
              paddingLeft: "8px",
              textTransform: "uppercase",
            }}
          >
            {text}
          </div>
        )
      } else if (isNumericOrAmount && record.groupTotal > 0) {
        return (
          <div
            style={{
              fontWeight: 700,
              color: "#000000",
              textAlign: textAlign,
              fontFamily: "Inter, sans-serif",
              fontSize: "9px",
            }}
          >
            {record.groupTotal.toLocaleString("en-US")}
          </div>
        )
      } else {
        return <div></div>
      }
    }

    // Handle subtotal rows
    if (record.isGroup && record.isSubtotal) {
      if (column.dataIndex === record.groupField) {
        return (
          <div
            style={{
              fontWeight: 400,
              color: "#000000",
              textAlign: "left",
              fontFamily: "Inter, sans-serif",
              fontSize: "9px",
              paddingLeft: "16px",
              fontStyle: "italic",
            }}
          >
            {text}
          </div>
        )
      } else {
        return (
          <div
            style={{
              fontWeight: 400,
              color: "#000000",
              textAlign: textAlign,
              fontFamily: "Inter, sans-serif",
              fontSize: "9px",
            }}
          >
            {isNumericOrAmount && text ? text.toLocaleString("en-US") : ""}
          </div>
        )
      }
    }

    // Handle regular data rows with drill-down and navigation functionality
    const isDrillDown = column.drillDown === "Y"
    const isNavigation = column.enableNavigation === "Y"
    const isClickable = isDrillDown || isNavigation

    return (
      <div
        style={{
          textAlign: textAlign,
          color: isClickable ? "#1A0DAB" : "#000000",
          fontFamily: "Inter, sans-serif",
          fontSize: "9px",
          paddingLeft: record.level > 0 ? "24px" : "8px",
          textDecoration: isClickable ? "underline" : "none",
          cursor: isClickable ? "pointer" : "default",
        }}
        onClick={() => {
          if (isDrillDown && onDrillDown) {
            onDrillDown(parametersToSend, column.dataIndex, record[column.dataIndex], column.detailReportId)
          } else if (isNavigation && onNavigation) {
            onNavigation(column, record)
          }
        }}
      >
        {isNumericOrAmount ? (text ? text.toLocaleString("en-US") : "") : text}
      </div>
    )
  }

  const calculateGrandTotal = () => {
    const totals = {}
    const dataRows = filteredData.filter((item) => !item.isGroup)

    orderedColumns.forEach((column) => {
      if (column.type === "Numeric" || column.type === "Amount") {
        totals[column.dataIndex] = dataRows.reduce((sum, item) => {
          return sum + Number.parseFloat(item[column.dataIndex] || 0)
        }, 0)
      }
    })

    return totals
  }

  const grandTotals = calculateGrandTotal()

  // Calculate column spans for grouped headers
  const getColumnSpan = (group) => {
    return group.columns.length
  }

  // Render grouped header structure
  const renderGroupedHeaders = () => {
    const hasGroups = groupedColumns.length > 0

    if (!hasGroups) {
      // Single row header when no groups
      return (
        <tr>
          {orderedColumns.map((column, index) => (
            <th key={column.dataIndex || index} style={{ position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontFamily: "Inter" }}>{column.title}</span>
              </div>
              {showFilterDropdown[column.dataIndex] && (
                <div className="filter-dropdown">
                  {column.type === "Date" ? (
                    <DatePicker
                      value={filters[column.dataIndex] ? moment(filters[column.dataIndex], "DD-MM-YYYY") : null}
                      onChange={(date, dateString) => {
                        const formattedDate = date ? moment(date).format("DD-MM-YYYY") : ""
                        handleFilterChange(column.dataIndex, formattedDate)
                      }}
                      style={{ marginBottom: 8, display: "block", width: "100%" }}
                    />
                  ) : (
                    <Input
                      value={filters[column.dataIndex] || ""}
                      onChange={(e) => handleFilterChange(column.dataIndex, e.target.value)}
                      placeholder={`Filter ${column.title}`}
                      style={{ marginBottom: 8, display: "block" }}
                    />
                  )}
                  <Space>
                    <Button onClick={() => handleFilterReset(column.dataIndex)} size="small">
                      Reset
                    </Button>
                    <Button
                      onClick={() => setShowFilterDropdown((prev) => ({ ...prev, [column.dataIndex]: false }))}
                      size="small"
                      type="primary"
                    >
                      OK
                    </Button>
                  </Space>
                </div>
              )}
            </th>
          ))}
        </tr>
      )
    }

    // Two-row header structure when groups exist
    return (
      <>
        {/* First row: Group headers */}
        <tr>
          {groupedColumns.map((group, groupIndex) => (
            <th
              key={`group-${group.id}`}
              colSpan={getColumnSpan(group)}
              style={{
                textAlign: "center",
                backgroundColor: "#f0f0f0",
                fontWeight: "bold",
                borderBottom: "1px solid #d9d9d9",
              }}
            >
              <span style={{ fontFamily: "Inter" }}>{group.displayName}</span>
            </th>
          ))}
          {ungroupedColumns.map((column, index) => (
            <th key={`ungrouped-${column.dataIndex || index}`} rowSpan={2} style={{ position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontFamily: "Inter" }}>{column.title}</span>
              </div>
              {showFilterDropdown[column.dataIndex] && (
                <div className="filter-dropdown">
                  {column.type === "Date" ? (
                    <DatePicker
                      value={filters[column.dataIndex] ? moment(filters[column.dataIndex], "DD-MM-YYYY") : null}
                      onChange={(date, dateString) => {
                        const formattedDate = date ? moment(date).format("DD-MM-YYYY") : ""
                        handleFilterChange(column.dataIndex, formattedDate)
                      }}
                      style={{ marginBottom: 8, display: "block", width: "100%" }}
                    />
                  ) : (
                    <Input
                      value={filters[column.dataIndex] || ""}
                      onChange={(e) => handleFilterChange(column.dataIndex, e.target.value)}
                      placeholder={`Filter ${column.title}`}
                      style={{ marginBottom: 8, display: "block" }}
                    />
                  )}
                  <Space>
                    <Button onClick={() => handleFilterReset(column.dataIndex)} size="small">
                      Reset
                    </Button>
                    <Button
                      onClick={() => setShowFilterDropdown((prev) => ({ ...prev, [column.dataIndex]: false }))}
                      size="small"
                      type="primary"
                    >
                      OK
                    </Button>
                  </Space>
                </div>
              )}
            </th>
          ))}
        </tr>

        {/* Second row: Individual column headers for grouped columns */}
        <tr>
          {groupedColumns.map((group, groupIndex) =>
            group.columns.map((column, colIndex) => (
              <th key={`grouped-${column.dataIndex || colIndex}`} style={{ position: "relative" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontFamily: "Inter" }}>{column.title}</span>
                </div>
                {showFilterDropdown[column.dataIndex] && (
                  <div className="filter-dropdown">
                    {column.type === "Date" ? (
                      <DatePicker
                        value={filters[column.dataIndex] ? moment(filters[column.dataIndex], "DD-MM-YYYY") : null}
                        onChange={(date, dateString) => {
                          const formattedDate = date ? moment(date).format("DD-MM-YYYY") : ""
                          handleFilterChange(column.dataIndex, formattedDate)
                        }}
                        style={{ marginBottom: 8, display: "block", width: "100%" }}
                      />
                    ) : (
                      <Input
                        value={filters[column.dataIndex] || ""}
                        onChange={(e) => handleFilterChange(column.dataIndex, e.target.value)}
                        placeholder={`Filter ${column.title}`}
                        style={{ marginBottom: 8, display: "block" }}
                      />
                    )}
                    <Space>
                      <Button onClick={() => handleFilterReset(column.dataIndex)} size="small">
                        Reset
                      </Button>
                      <Button
                        onClick={() => setShowFilterDropdown((prev) => ({ ...prev, [column.dataIndex]: false }))}
                        size="small"
                        type="primary"
                      >
                        OK
                      </Button>
                    </Space>
                  </div>
                )}
              </th>
            )),
          )}
        </tr>
      </>
    )
  }

  return (
    <div
      style={{
        fontFamily: "Inter, sans-serif",
        backgroundColor: "#ffffff",
        display: "inline-block",
        width: "auto",
        position: "relative",
      }}
    >
      <style>
        {`
          .custom-table-container {
            display: inline-block;
            width: auto;
            min-width: fit-content;
            overflow: auto;
            max-height: ${fullScreen ? "70vh" : "66vh"};
          }

          .custom-table {
            border-collapse: collapse;
            font-family: "Inter, sans-serif";
            font-size: 12px;
            width: auto;
            min-width: fit-content;
            border: 1px solid #d9d9d9;
            background-color: #ffffff;
          }

          .custom-table th {
            background-color: #f5f5f5;
            border: 1px solid #d9d9d9;
            font-weight: 400;
            font-family: "Inter, sans-serif";
            font-size: 9px;
            color: #000000;
            padding: 4px 12px;
            white-space: nowrap;
            position: relative;
            text-align: left;
          }

          .custom-table td {
            border: 1px solid #d9d9d9;
            padding: 4px 10px;
            font-family: "Inter, sans-serif";
            font-size: 6px;
            color: #000000;
            white-space: nowrap;
          }

          .custom-table tbody tr:hover td {
            background-color: #f0f0f0;
          }

          .group-header-row {
            background-color: #f8f9fa !important;
          }

          .group-header-row td {
            background-color: #f8f9fa !important;
            font-weight: 700;
          }

          .subtotal-row {
            background-color: #f8f9fa !important;
            border-top: 1px solid #dee2e6 !important;
          }

          .subtotal-row td {
            background-color: #f8f9fa !important;
            border-top: 1px solid #dee2e6 !important;
            font-weight: 600;
          }

          .grand-total-row {
            background-color: #e9ecef !important;
            border-top: 2px solid #d9d9d9 !important;
          }

          .grand-total-row td {
            background-color: #e9ecef !important;
            border-top: 2px solid #d9d9d9 !important;
            font-weight: 400;
            font-size: 9px;
          }

          .filter-dropdown {
            position: absolute;
            top: 100%;
            left: 0;
            background: white;
            border: 1px solid #d9d9d9;
            border-radius: 4px;
            padding: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            z-index: 1000;
            min-width: 200px;
          }

          .filter-icon {
            cursor: pointer;
            margin-left: 4px;
            opacity: 0.6;
          }

          .filter-icon:hover {
            opacity: 1;
          }

          .filter-icon.active {
            opacity: 1;
            color: #1890ff;
          }
        `}
      </style>

      <div className="custom-table-container">
        <table className="custom-table">
          <thead>{renderGroupedHeaders()}</thead>
          <tbody>
            {filteredData.map((record, rowIndex) => (
              <tr
                key={record.key || rowIndex}
                className={
                  record.isGroup && record.isGroupHeader
                    ? "group-header-row"
                    : record.isGroup && record.isSubtotal
                      ? "subtotal-row"
                      : ""
                }
              >
                {/* Render columns in the new order: grouped first, then ungrouped */}
                {orderedColumns.map((column, colIndex) => (
                  <td
                    key={`${record.key || rowIndex}-${column.dataIndex || colIndex}`}
                    onClick={() => {
                      if (!record.isGroup) {
                        if (column.drillDown === "Y" && onDrillDown) {
                          onDrillDown(
                            parametersToSend,
                            column.dataIndex,
                            record[column.dataIndex],
                            column.detailReportId,
                          )
                        } else if (column.enableNavigation === "Y" && onNavigation) {
                          onNavigation(column, record)
                        }
                      }
                    }}
                    style={{
                      cursor:
                        !record.isGroup && (column.drillDown === "Y" || column.enableNavigation === "Y")
                          ? "pointer"
                          : "default",
                    }}
                  >
                    {renderCell(record, column)}
                  </td>
                ))}
              </tr>
            ))}

            {/* Grand Total Row */}
            <tr className="grand-total-row">
              {orderedColumns.map((column, index) => (
                <td key={`grand-total-${column.dataIndex || index}`}>
                  <div
                    style={{
                      textAlign:
                        index === 0 ? "left" : column.type === "Numeric" || column.type === "Amount" ? "right" : "left",
                      fontWeight: "400",
                      fontFamily: "Inter, sans-serif",
                      color: "#000000",
                      fontSize: "9px",
                    }}
                  >
                    {index === 0
                      ? "GRAND TOTAL"
                      : (column.type === "Numeric" || column.type === "Amount") && grandTotals[column.dataIndex]
                        ? grandTotals[column.dataIndex].toLocaleString("en-US")
                        : ""}
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default AnalyticsReportTable
